import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createDropdownOptions } from '../../shared/utility/helper';
import { filterByEntity } from '../utility/evidenceRecordsHelper';
import { PARTY_TYPE_ENTITY } from '../constants/evidenceRecordsFilters';

const useEvidenceDictionaryOptions = () => {
  const dictionary = useSelector((state) => state.dictionary);

  const disposalActions = useMemo(() => {
    return createDropdownOptions(dictionary?.codeEvidenceDisposalCode ?? [], {
      labelField: 'Code',
      valueField: 'PtsCodeID',
      descriptionField: 'Description',
    });
  }, [dictionary?.codeEvidenceDisposalCode]);

  const requiredActions = useMemo(() => {
    return createDropdownOptions(dictionary?.codeEvidenceRequiredAction ?? [], {
      labelField: 'Code',
      valueField: 'PtsCodeID',
      descriptionField: 'Description',
    });
  }, [dictionary?.codeEvidenceRequiredAction]);

  const custodyStatuses = useMemo(() => {
    return createDropdownOptions(dictionary?.codeEvidenceCustodyStatus ?? [], {
      labelField: 'Code',
      valueField: 'PtsCodeID',
      extraFields: ['Category', 'IsDefault'],
      descriptionField: 'Description',
    });
  }, [dictionary?.codeEvidenceCustodyStatus]);

  const propertyCategory = useMemo(() => {
    return createDropdownOptions(dictionary?.codeEvidenceCategory ?? [], {
      labelField: 'Code',
      valueField: 'PtsCodeID',
    });
  }, [dictionary?.codeEvidenceCategory]);

  const propertyCategoryFilterOptions = useMemo(() => {
    return createDropdownOptions(dictionary?.codeEvidenceCategory ?? [], {
      labelField: 'Code',
      valueField: 'PtsCodeID',
      descriptionField: 'Description',
    });
  }, [dictionary?.codeEvidenceCategory]);

  const evidenceTypes = useMemo(() => {
    return createDropdownOptions(dictionary?.codeEvidenceType ?? [], {
      labelField: 'Code',
      valueField: 'PtsCodeID',
      descriptionField: 'Description',
    });
  }, [dictionary?.codeEvidenceType]);

  const propertyTypes = useMemo(() => {
    return createDropdownOptions(dictionary?.codeEvidencePropertyType ?? [], {
      labelField: 'Code',
      valueField: 'PtsCodeID',
      extraFields: ['ShowPhysical', 'ShowSubstance'],
      descriptionField: 'Description',
    });
  }, [dictionary?.codeEvidencePropertyType]);

  const substanceCategories = useMemo(() => {
    return createDropdownOptions(dictionary?.codeSubstanceCategory ?? [], {
      labelField: 'Code',
      valueField: 'PtsCodeID',
      descriptionField: 'Description',
    });
  }, [dictionary?.codeSubstanceCategory]);

  const drugTypes = useMemo(() => {
    return createDropdownOptions(dictionary?.codeSuspectedDrugTypes ?? [], {
      labelField: 'Code',
      valueField: 'PtsCodeID',
      descriptionField: 'Description',
    });
  }, [dictionary?.codeSuspectedDrugTypes]);

  const measurementOptions = useMemo(() => {
    return createDropdownOptions(dictionary?.codeDrugMeasurements ?? [], {
      labelField: 'Code',
      valueField: 'PtsCodeID',
      descriptionField: 'Description',
    });
  }, [dictionary?.codeDrugMeasurements]);

  const howDestroyedOptions = useMemo(() => {
    return createDropdownOptions(dictionary?.codeEvidenceDestructionWays ?? [], {
      labelField: 'Code',
      valueField: 'PtsCodeID',
      descriptionField: 'Description',
    });
  }, [dictionary?.codeEvidenceDestructionWays]);

  const dispositionOptions = useMemo(() => {
    return createDropdownOptions(dictionary?.codeEvidenceFinalDisposition ?? [], {
      labelField: 'Code',
      valueField: 'PtsCodeID',
      descriptionField: 'Description',
    });
  }, [dictionary?.codeEvidenceFinalDisposition]);

  const storageLocationOptions = useMemo(() => {
    return createDropdownOptions(dictionary?.codeEvidenceStorageLocation ?? [], {
      labelField: 'Code',
      valueField: 'PtsCodeID',
      descriptionField: 'Description',
    });
  }, [dictionary?.codeEvidenceStorageLocation]);

  const containerOptions = useMemo(() => {
    return createDropdownOptions(dictionary?.codeEvidenceStorageContainer ?? [], {
      labelField: 'Code',
      valueField: 'PtsCodeID',
      descriptionField: 'Description',
    });
  }, [dictionary?.codeEvidenceStorageContainer]);

  const allSubLocationOptions = useMemo(() => {
    return createDropdownOptions(dictionary?.codeEvidenceStorageSubLocation ?? [], {
      labelField: 'Code',
      valueField: 'PtsCodeID',
      extraFields: ['ParentCode'],
      descriptionField: 'Description',
    });
  }, [dictionary?.codeEvidenceStorageSubLocation]);

  const custodyReasonOptions = useMemo(() => {
    return createDropdownOptions(dictionary?.codeEvidenceCustodyReason ?? [], {
      labelField: 'Code',
      valueField: 'PtsCodeID',
      descriptionField: 'Description',
    });
  }, [dictionary?.codeEvidenceCustodyReason]);

  const checkoutLocationOptions = useMemo(() => {
    return createDropdownOptions(dictionary?.codeEvidenceCheckoutLocation ?? [], {
      labelField: 'Code',
      valueField: 'PtsCodeID',
      descriptionField: 'Description',
    });
  }, [dictionary?.codeEvidenceCheckoutLocation]);

  const checkoutReasonOptions = useMemo(() => {
    return createDropdownOptions(dictionary?.codeEvidenceCheckoutReason ?? [], {
      labelField: 'Code',
      valueField: 'PtsCodeID',
      descriptionField: 'Description',
    });
  }, [dictionary?.codeEvidenceCheckoutReason]);

  const temporaryLocationOptions = useMemo(() => {
    return createDropdownOptions(dictionary?.codeEvidenceTemporaryLocation ?? [], {
      labelField: 'Code',
      valueField: 'PtsCodeID',
      descriptionField: 'Description',
    });
  }, [dictionary?.codeEvidenceTemporaryLocation]);

  const correctionReasonOptions = useMemo(() => {
    return createDropdownOptions(dictionary?.codeEvidenceCorrectionReason ?? [], {
      labelField: 'Code',
      valueField: 'PtsCodeID',
      descriptionField: 'Description',
    });
  }, [dictionary?.codeEvidenceCorrectionReason]);

  const offenseOptions = useMemo(() => {
    return createDropdownOptions(dictionary?.ptsStatute ?? [], {
      labelField: 'Description',
      valueField: 'ptsStatuteID',
    });
  }, [dictionary?.ptsStatute]);

  const partyTypeOptions = useMemo(() => {
    return createDropdownOptions(
      filterByEntity(dictionary?.codePartyRelationship, PARTY_TYPE_ENTITY),
      {
        labelField: 'Code',
        valueField: 'PtsCodeID',
        descriptionField: 'Description',
      }
    );
  }, [dictionary?.codePartyRelationship]);

  const partyTypeCodeOptions = useMemo(() => {
    return createDropdownOptions(
      filterByEntity(dictionary?.codePartyRelationship, PARTY_TYPE_ENTITY),
      {
        labelField: 'Code',
        valueField: 'Code',
      }
    );
  }, [dictionary?.codePartyRelationship]);

  const prefixOptions = useMemo(() => dictionary?.codeNamePrefixes ?? [], [
    dictionary?.codeNamePrefixes,
  ]);
  const suffixOptions = useMemo(() => dictionary?.codeNameSuffixes ?? [], [
    dictionary?.codeNameSuffixes,
  ]);
  const raceOptions = useMemo(() => dictionary?.codeRace ?? [], [dictionary?.codeRace]);
  const sexOptions = useMemo(() => dictionary?.codeSex ?? [], [dictionary?.codeSex]);
  const ethnicityOptions = useMemo(() => dictionary?.codeEthnicity ?? [], [
    dictionary?.codeEthnicity,
  ]);
  const ageRangeOptions = useMemo(() => dictionary?.ageIncrement ?? [], [dictionary?.ageIncrement]);

  const eyeColorOptions = useMemo(() => dictionary?.codeEyeColor ?? [], [dictionary?.codeEyeColor]);
  const hairColorOptions = useMemo(() => dictionary?.codeHairColor ?? [], [
    dictionary?.codeHairColor,
  ]);
  const hairStyleOptions = useMemo(() => dictionary?.codeHairStyle ?? [], [
    dictionary?.codeHairStyle,
  ]);
  const hairLengthOptions = useMemo(() => dictionary?.codeHairLength ?? [], [
    dictionary?.codeHairLength,
  ]);
  const bloodTypeOptions = useMemo(() => dictionary?.codeBloodType ?? [], [
    dictionary?.codeBloodType,
  ]);
  const handedOptions = useMemo(() => dictionary?.codeHanded ?? [], [dictionary?.codeHanded]);
  const stateOptions = useMemo(() => dictionary?.codeStates ?? [], [dictionary?.codeStates]);
  const streetDirectionOptions = useMemo(() => dictionary?.codeStreetDirections ?? [], [
    dictionary?.codeStreetDirections,
  ]);
  const streetTypeOptions = useMemo(() => dictionary?.codeStreetTypes ?? [], [
    dictionary?.codeStreetTypes,
  ]);
  const cityOptions = useMemo(() => {
    return (dictionary?.codeCities ?? []).map((item) => ({
      ...item,
      Code: item.ptsCityID,
      Description: item.CityDescription,
    }));
  }, [dictionary?.codeCities]);

  const countyOptions = useMemo(() => dictionary?.codeCounties ?? [], [dictionary?.codeCounties]);
  const countryOptions = useMemo(() => dictionary?.codeCountries ?? [], [
    dictionary?.codeCountries,
  ]);
  const deathCauseOptions = useMemo(() => dictionary?.codeDeathCauses ?? [], [
    dictionary?.codeDeathCauses,
  ]);
  const residentOptions = useMemo(() => dictionary?.codeResident ?? [], [dictionary?.codeResident]);
  const citizenshipOptions = useMemo(() => dictionary?.codeCitizenships ?? [], [
    dictionary?.codeCitizenships,
  ]);
  const nationalityOptions = useMemo(() => dictionary?.codeNationalities ?? [], [
    dictionary?.codeNationalities,
  ]);
  const gangOptions = useMemo(() => dictionary?.codeGang ?? [], [dictionary?.codeGang]);
  const maritalStatusOptions = useMemo(() => dictionary?.codeMaritalStatus ?? [], [
    dictionary?.codeMaritalStatus,
  ]);
  const languageOptions = useMemo(() => dictionary?.codeLanguages ?? [], [
    dictionary?.codeLanguages,
  ]);
  const dangerousTypeOptions = useMemo(() => dictionary?.codeDangerousType ?? [], [
    dictionary?.codeDangerousType,
  ]);
  const contactTypeOptions = useMemo(() => dictionary?.codeContactInfoMethodTypes ?? [], [
    dictionary?.codeContactInfoMethodTypes,
  ]);
  const unitTypeOptions = useMemo(() => dictionary?.codeAddressUnitTypes ?? [], [
    dictionary?.codeAddressUnitTypes,
  ]);
  const addressCategoryOptions = useMemo(() => dictionary?.codeAddressCategories ?? [], [
    dictionary?.codeAddressCategories,
  ]);
  const floorTypeOptions = useMemo(() => dictionary?.codeAddressFloorTypes ?? [], [
    dictionary?.codeAddressFloorTypes,
  ]);
  const bldgTypeOptions = useMemo(() => dictionary?.codeAddressBldgTypes ?? [], [
    dictionary?.codeAddressBldgTypes,
  ]);
  const esnOptions = useMemo(() => dictionary?.cadESNs ?? [], [dictionary?.cadESNs]);
  const nameTypeOptions = useMemo(() => dictionary?.codePersonNameTypes ?? [], [
    dictionary?.codePersonNameTypes,
  ]);
  const contactMethodOptions = useMemo(() => dictionary?.ContactMethods ?? [], [
    dictionary?.ContactMethods,
  ]);
  const credentialTypeOptions = useMemo(() => dictionary?.codeCredentialTypes ?? [], [
    dictionary?.codeCredentialTypes,
  ]);
  const credentialClassOptions = useMemo(() => dictionary?.codeCredentialClasses ?? [], [
    dictionary?.codeCredentialClasses,
  ]);
  const positionOptions = useMemo(() => dictionary?.codePositions ?? [], [
    dictionary?.codePositions,
  ]);
  const occupationOptions = useMemo(() => dictionary?.codeOccupations ?? [], [
    dictionary?.codeOccupations,
  ]);
  const rankOptions = useMemo(() => dictionary?.codeRanks ?? [], [dictionary?.codeRanks]);
  const employmentStatusOptions = useMemo(() => dictionary?.codeEmploymentStatuses ?? [], [
    dictionary?.codeEmploymentStatuses,
  ]);
  const payRateOptions = useMemo(() => dictionary?.PayRates ?? [], [dictionary?.PayRates]);
  const terminationReasonOptions = useMemo(() => dictionary?.codeTerminationReasons ?? [], [
    dictionary?.codeTerminationReasons,
  ]);
  const dnaSampleTypeOptions = useMemo(() => dictionary?.codeDNASampleType ?? [], [
    dictionary?.codeDNASampleType,
  ]);
  const dnaSampleMethodOptions = useMemo(() => dictionary?.codeDNASampleMethod ?? [], [
    dictionary?.codeDNASampleMethod,
  ]);
  const dnaSampleReasonOptions = useMemo(() => dictionary?.codeDNASampleReason ?? [], [
    dictionary?.codeDNASampleReason,
  ]);
  const dnaCollectionStatusOptions = useMemo(() => dictionary?.codeDNACollection ?? [], [
    dictionary?.codeDNACollection,
  ]);
  const associateRelationshipOptions = useMemo(() => dictionary?.codeAssociateRelationships ?? [], [
    dictionary?.codeAssociateRelationships,
  ]);
  const partyKindOptions = useMemo(() => dictionary?.PartyTypes ?? [], [dictionary?.PartyTypes]);
  const plateTypeOptions = useMemo(() => dictionary?.codePlateTypes ?? [], [
    dictionary?.codePlateTypes,
  ]);
  const plateColorOptions = useMemo(() => dictionary?.codePlateColors ?? [], [
    dictionary?.codePlateColors,
  ]);
  const plateStatusOptions = useMemo(() => dictionary?.codePlateStatuses ?? [], [
    dictionary?.codePlateStatuses,
  ]);
  const plateDetailOptions = useMemo(() => dictionary?.codePlateDetails ?? [], [
    dictionary?.codePlateDetails,
  ]);
  const tagTypeOptions = useMemo(() => dictionary?.codeTagTypes ?? [], [dictionary?.codeTagTypes]);

  const placeTypeOptions = useMemo(() => dictionary?.codePlaceTypes ?? [], [
    dictionary?.codePlaceTypes,
  ]);

  const vehicleMakeOptions = useMemo(() => dictionary?.codeVehicleMakes ?? [], [
    dictionary?.codeVehicleMakes,
  ]);
  const vehicleMakesModelsOptions = useMemo(() => dictionary?.codeVehicleMakesModels ?? [], [
    dictionary?.codeVehicleMakesModels,
  ]);
  const vehicleStyleOptions = useMemo(() => dictionary?.codeVehicleStyles ?? [], [
    dictionary?.codeVehicleStyles,
  ]);
  const vehicleColorOptions = useMemo(() => dictionary?.codeVehicleColors ?? [], [
    dictionary?.codeVehicleColors,
  ]);
  const vehicleClassificationOptions = useMemo(() => dictionary?.codeVehicleClassifications ?? [], [
    dictionary?.codeVehicleClassifications,
  ]);
  const vehicleBrandOptions = useMemo(() => dictionary?.codeVehicleBrands ?? [], [
    dictionary?.codeVehicleBrands,
  ]);
  const vehicleBranderOptions = useMemo(() => dictionary?.codeVehicleBranders ?? [], [
    dictionary?.codeVehicleBranders,
  ]);
  const vehiclePropertyOptions = useMemo(() => dictionary?.codeVehicleProperty ?? [], [
    dictionary?.codeVehicleProperty,
  ]);
  const vehicleCommercialOptions = useMemo(() => dictionary?.codeVehicleCommercial ?? [], [
    dictionary?.codeVehicleCommercial,
  ]);
  const transmissionOptions = useMemo(() => dictionary?.codeTransmissions ?? [], [
    dictionary?.codeTransmissions,
  ]);
  const garageFacilityOptions = useMemo(() => dictionary?.codeGarageFacilities ?? [], [
    dictionary?.codeGarageFacilities,
  ]);

  const attachmentTypeOptions = useMemo(() => dictionary?.codeAttachmentTypes ?? [], [
    dictionary?.codeAttachmentTypes,
  ]);

  const measureOptions = useMemo(() => dictionary?.codeMeasures ?? [], [dictionary?.codeMeasures]);
  const addressPartyRelationshipOptions = useMemo(
    () => dictionary?.codeAddressPartyRelationship ?? [],
    [dictionary?.codeAddressPartyRelationship]
  );
  const prePlanOccupancyTypeOptions = useMemo(() => dictionary?.codePrePlanOccupancyTypes ?? [], [
    dictionary?.codePrePlanOccupancyTypes,
  ]);
  const prePlanConstructionTypeOptions = useMemo(
    () => dictionary?.codePrePlanConstructionTypes ?? [],
    [dictionary?.codePrePlanConstructionTypes]
  );
  const prePlanRoofTypeOptions = useMemo(() => dictionary?.codePrePlanRoofTypes ?? [], [
    dictionary?.codePrePlanRoofTypes,
  ]);
  const designationOptions = useMemo(() => dictionary?.codeDesignation ?? [], [
    dictionary?.codeDesignation,
  ]);

  const mugshotTypeOptions = useMemo(() => dictionary?.codeMugshotTypes ?? [], [
    dictionary?.codeMugshotTypes,
  ]);
  const markTypeOptions = useMemo(() => dictionary?.codeMarks ?? [], [dictionary?.codeMarks]);
  const markLocationOptions = useMemo(() => dictionary?.codeMarkLocation ?? [], [
    dictionary?.codeMarkLocation,
  ]);

  const alertTypeOptions = useMemo(() => dictionary?.AlertTypes ?? [], [dictionary?.AlertTypes]);
  const agencyOptions = useMemo(() => dictionary?.Agencies ?? [], [dictionary?.Agencies]);

  const personPartyTypeOptions = useMemo(
    () => filterByEntity(dictionary?.codePartyRelationship, PARTY_TYPE_ENTITY),
    [dictionary?.codePartyRelationship]
  );

  return useMemo(
    () => ({
      dictionary,
      disposalActions,
      requiredActions,
      custodyStatuses,
      propertyCategory,
      propertyCategoryFilterOptions,
      evidenceTypes,
      propertyTypes,
      substanceCategories,
      drugTypes,
      measurementOptions,
      howDestroyedOptions,
      dispositionOptions,
      storageLocationOptions,
      containerOptions,
      allSubLocationOptions,
      custodyReasonOptions,
      checkoutLocationOptions,
      checkoutReasonOptions,
      temporaryLocationOptions,
      correctionReasonOptions,
      offenseOptions,
      partyTypeOptions,
      partyTypeCodeOptions,
      prefixOptions,
      suffixOptions,
      raceOptions,
      sexOptions,
      ethnicityOptions,
      ageRangeOptions,
      eyeColorOptions,
      hairColorOptions,
      hairStyleOptions,
      hairLengthOptions,
      bloodTypeOptions,
      handedOptions,
      stateOptions,
      streetDirectionOptions,
      streetTypeOptions,
      cityOptions,
      countyOptions,
      countryOptions,
      deathCauseOptions,
      residentOptions,
      citizenshipOptions,
      nationalityOptions,
      gangOptions,
      maritalStatusOptions,
      languageOptions,
      dangerousTypeOptions,
      contactTypeOptions,
      unitTypeOptions,
      addressCategoryOptions,
      floorTypeOptions,
      bldgTypeOptions,
      esnOptions,
      nameTypeOptions,
      contactMethodOptions,
      credentialTypeOptions,
      credentialClassOptions,
      positionOptions,
      occupationOptions,
      rankOptions,
      employmentStatusOptions,
      payRateOptions,
      terminationReasonOptions,
      dnaSampleTypeOptions,
      dnaSampleMethodOptions,
      dnaSampleReasonOptions,
      dnaCollectionStatusOptions,
      associateRelationshipOptions,
      partyKindOptions,
      plateTypeOptions,
      plateColorOptions,
      plateStatusOptions,
      plateDetailOptions,
      tagTypeOptions,
      attachmentTypeOptions,
      mugshotTypeOptions,
      markTypeOptions,
      markLocationOptions,
      alertTypeOptions,
      agencyOptions,
      personPartyTypeOptions,
      placeTypeOptions,
      vehicleMakeOptions,
      vehicleMakesModelsOptions,
      vehicleStyleOptions,
      vehicleColorOptions,
      vehicleClassificationOptions,
      vehicleBrandOptions,
      vehicleBranderOptions,
      vehiclePropertyOptions,
      vehicleCommercialOptions,
      transmissionOptions,
      garageFacilityOptions,
      measureOptions,
      addressPartyRelationshipOptions,
      prePlanOccupancyTypeOptions,
      prePlanConstructionTypeOptions,
      prePlanRoofTypeOptions,
      designationOptions,
    }),
    [
      dictionary,
      disposalActions,
      requiredActions,
      custodyStatuses,
      propertyCategory,
      propertyCategoryFilterOptions,
      evidenceTypes,
      propertyTypes,
      substanceCategories,
      drugTypes,
      measurementOptions,
      howDestroyedOptions,
      dispositionOptions,
      storageLocationOptions,
      containerOptions,
      allSubLocationOptions,
      custodyReasonOptions,
      checkoutLocationOptions,
      checkoutReasonOptions,
      temporaryLocationOptions,
      correctionReasonOptions,
      offenseOptions,
      partyTypeOptions,
      partyTypeCodeOptions,
      prefixOptions,
      suffixOptions,
      raceOptions,
      sexOptions,
      ethnicityOptions,
      ageRangeOptions,
      eyeColorOptions,
      hairColorOptions,
      hairStyleOptions,
      hairLengthOptions,
      bloodTypeOptions,
      handedOptions,
      stateOptions,
      streetDirectionOptions,
      streetTypeOptions,
      cityOptions,
      countyOptions,
      countryOptions,
      deathCauseOptions,
      residentOptions,
      citizenshipOptions,
      nationalityOptions,
      gangOptions,
      maritalStatusOptions,
      languageOptions,
      dangerousTypeOptions,
      contactTypeOptions,
      unitTypeOptions,
      addressCategoryOptions,
      floorTypeOptions,
      bldgTypeOptions,
      esnOptions,
      nameTypeOptions,
      contactMethodOptions,
      credentialTypeOptions,
      credentialClassOptions,
      positionOptions,
      occupationOptions,
      rankOptions,
      employmentStatusOptions,
      payRateOptions,
      terminationReasonOptions,
      dnaSampleTypeOptions,
      dnaSampleMethodOptions,
      dnaSampleReasonOptions,
      dnaCollectionStatusOptions,
      associateRelationshipOptions,
      partyKindOptions,
      plateTypeOptions,
      plateColorOptions,
      plateStatusOptions,
      plateDetailOptions,
      tagTypeOptions,
      attachmentTypeOptions,
      mugshotTypeOptions,
      markTypeOptions,
      markLocationOptions,
      alertTypeOptions,
      agencyOptions,
      personPartyTypeOptions,
      placeTypeOptions,
      vehicleMakeOptions,
      vehicleMakesModelsOptions,
      vehicleStyleOptions,
      vehicleColorOptions,
      vehicleClassificationOptions,
      vehicleBrandOptions,
      vehicleBranderOptions,
      vehiclePropertyOptions,
      vehicleCommercialOptions,
      transmissionOptions,
      garageFacilityOptions,
      measureOptions,
      addressPartyRelationshipOptions,
      prePlanOccupancyTypeOptions,
      prePlanConstructionTypeOptions,
      prePlanRoofTypeOptions,
      designationOptions,
    ]
  );
};

export default useEvidenceDictionaryOptions;
